import type { Notification } from "@loot-os/api-contract";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "@loot-os/ui/components/item";
import {
  formatNotificationTime,
  NOTIFICATION_CATEGORY_TILE,
  NOTIFICATION_ICON_COMPONENTS,
} from "@loot-os/ui/lib/notifications";
import { cn } from "@loot-os/ui/lib/utils";
import { ChevronRight } from "lucide-react";
import type * as React from "react";

/** A notification's icon on its category's tinted tile. */
export function NotificationIconTile({
  category,
  icon,
  className,
}: Pick<Notification, "category" | "icon"> & { className?: string }) {
  const Icon = NOTIFICATION_ICON_COMPONENTS[icon];
  return (
    <ItemMedia
      variant="icon"
      className={cn(
        "size-9 rounded-lg [&_svg:not([class*='size-'])]:size-4.5",
        NOTIFICATION_CATEGORY_TILE[category],
        className,
      )}
    >
      <Icon aria-hidden />
    </ItemMedia>
  );
}

type NotificationItemProps = Omit<React.ComponentProps<typeof Item>, "children"> & {
  notification: Pick<
    Notification,
    "category" | "icon" | "title" | "body" | "imageUrl" | "action" | "createdAt" | "readAt"
  >;
  now: Date;
};

/**
 * One notification row: icon tile, title, body, optional banner and action label, its age and an
 * unread dot. Pass `render` to make the row the link or button; it holds nothing interactive.
 */
export function NotificationItem({
  notification,
  now,
  className,
  ...props
}: NotificationItemProps) {
  const unread = notification.readAt === null;
  return (
    <Item
      size="sm"
      data-unread={unread || undefined}
      className={cn(
        "items-start text-left [button]:cursor-pointer [button]:hover:bg-muted",
        className,
      )}
      {...props}
    >
      <NotificationIconTile category={notification.category} icon={notification.icon} />
      <ItemContent className="min-w-0 gap-0.5">
        <ItemTitle className="line-clamp-2 w-full">{notification.title}</ItemTitle>
        {notification.body ? (
          <ItemDescription className="line-clamp-3">{notification.body}</ItemDescription>
        ) : null}
        {notification.imageUrl ? (
          <img
            src={notification.imageUrl}
            alt=""
            loading="lazy"
            className="mt-2 aspect-[3/1] w-full rounded-md bg-muted object-cover"
          />
        ) : null}
        {notification.action ? (
          <span className="mt-1.5 inline-flex items-center gap-0.5 text-xs font-medium text-primary">
            {notification.action.label}
            <ChevronRight aria-hidden className="size-3.5" />
          </span>
        ) : null}
      </ItemContent>
      <ItemActions className="flex-col items-end gap-1.5 self-start pt-0.5">
        <time dateTime={notification.createdAt} className="text-xs text-muted-foreground">
          {formatNotificationTime(notification.createdAt, now)}
        </time>
        {unread ? (
          <span className="size-2 rounded-full bg-primary">
            <span className="sr-only">Unread</span>
          </span>
        ) : null}
      </ItemActions>
    </Item>
  );
}
